import { useEffect, useState } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { clearAuth, getAuth, onAuthChange } from './lib/api';
import type { AdminAuth } from './lib/api';
import { ThemeToggle } from './components/ThemeToggle';
import { Login } from './pages/Login';
import { Overview } from './pages/Overview';
import { Keys } from './pages/Keys';
import { Tasks } from './pages/Tasks';
import { Profiles } from './pages/Profiles';
import { Samples } from './pages/Samples';
import { Dictionary } from './pages/Dictionary';
import { Unknowns } from './pages/Unknowns';
import { Models } from './pages/Models';
import Logs from './pages/Logs';
import Upstream from './pages/Upstream';
import Audit from './pages/Audit';
import Playground from './pages/Playground';
import { NotFound } from './pages/ComingSoon';

interface NavItem {
  to: string;
  label: string;
  /** Co gia tri = muc thuoc phase sau, chi tro toi trang giai thich. */
  phase?: string;
}

/**
 * Thanh dieu huong liet ke ca cac muc cua phase sau.
 *
 * Ly do de chung o day tu bay gio: nhin mot cai la biet san pham se di den dau,
 * va khong ai phai doan xem "gan nhan tay o dau". Muc chua lam duoc danh dau ro rang
 * va dan toi trang giai thich — khong bao gio dan toi bang so lieu gia.
 */
const NAV: ReadonlyArray<NavItem> = [
  { to: '/', label: 'Tổng quan' },
  { to: '/playground', label: 'Thử giải' },
  { to: '/keys', label: 'API key' },
  { to: '/tasks', label: 'Task' },
  { to: '/profiles', label: 'Profile' },
  { to: '/models', label: 'Model' },
  { to: '/unknowns', label: 'Captcha lạ' },
  { to: '/samples', label: 'Mẫu' },
  { to: '/dictionary', label: 'Từ điển hash' },
  { to: '/logs', label: 'Log trực tiếp' },
  { to: '/audit', label: 'Nhật ký' },
  { to: '/upstream', label: 'Upstream' },
];

export default function App() {
  const [auth, setAuthState] = useState<AdminAuth | null>(() => getAuth());

  // api.ts tu goi clearAuth() khi gap 401/403, nen man hinh dan key hien lai
  // ma khong trang nao phai tu xu ly.
  useEffect(() => onAuthChange(setAuthState), []);

  if (auth === null) {
    return <Login />;
  }

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-bold tracking-tight">capforge</span>
            <span className="hidden text-xs text-slate-500 dark:text-slate-400 sm:inline">
              bang dieu khien
            </span>
          </div>

          <nav className="flex flex-1 flex-wrap items-center gap-1" aria-label="Điều hướng chính">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  [
                    'rounded px-2 py-1 text-sm transition-colors',
                    isActive
                      ? 'bg-sky-100 font-semibold text-sky-900 dark:bg-sky-950 dark:text-sky-200'
                      : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                    item.phase !== undefined ? 'opacity-70' : '',
                  ].join(' ')
                }
                title={item.phase !== undefined ? `Se lam o ${item.phase}` : undefined}
              >
                {item.label}
                {item.phase !== undefined && (
                  <span className="ml-1 rounded bg-slate-200 px-1 text-[10px] font-medium uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    phase sau
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <span
              className="hidden text-xs text-slate-500 dark:text-slate-400 md:inline"
              title={auth.mode === 'bearer' ? 'Authorization: Bearer' : 'X-Admin-Token'}
            >
              {auth.mode === 'bearer' ? 'admin key' : 'admin token'}
            </span>
            <ThemeToggle />
            <button
              type="button"
              className="cf-btn cf-btn-ghost"
              onClick={() => clearAuth('Đã đăng xuất.')}
            >
              Dang xuat
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-5">
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/keys" element={<Keys />} />
          <Route path="/tasks" element={<Tasks />} />

          <Route path="/profiles" element={<Profiles />} />
          <Route path="/models" element={<Models />} />

          <Route path="/unknowns" element={<Unknowns />} />
          <Route path="/samples" element={<Samples />} />
          <Route path="/dictionary" element={<Dictionary />} />
          <Route path="/logs" element={<Logs />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/upstream" element={<Upstream />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <footer className="mx-auto max-w-[1600px] px-4 pb-6 text-xs text-slate-500 dark:text-slate-400">
        capforge — dịch vụ giải captcha tự host, tương thích API 2Captcha. Phase 0–6: 5 solver (text, ocr, hashgrid, pow, rotate), log trực tiếp, upstream tuỳ chọn.
      </footer>
    </div>
  );
}
